import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * O "⋯" do HUB: onde moram as ações RARAS e as DESTRUTIVAS (§8 e §8.1 do guia).
 *
 * Extraído do PanelHeader para o rodapé dos modais usar o mesmo menu, em vez de
 * cada tela pendurar um botão colorido a mais ao lado da ação primária. No rodapé
 * ele abre PARA CIMA (`abrePara='cima'`), porque abaixo não há espaço.
 */
export interface ItemDeMenu {
  /** Texto do item; pode levar uma segunda linha (o motivo de uma trava, por exemplo). */
  label: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
  /** Rótulo de seção; quando true, `label` vira cabeçalho e `onClick` é ignorado. */
  section?: boolean;
  /**
   * Um traço antes do item (10/10/2026). O "⋯" da linha de Despesas separa o Excluir (sem volta) do Cancelar
   * (reversível): eram vizinhos e idênticos, e o traço é parte da distinção.
   */
  separadoAntes?: boolean;
}

interface Props {
  itens: ItemDeMenu[];
  abrePara?: 'baixo' | 'cima';
  title?: string;
  /**
   * Com rótulo, o gatilho deixa de ser o "⋯" e vira um botão secundário com nome e seta (ex.: o
   * "Exportar" das Faturas, que junta três exportações num lugar só). O menu é o mesmo.
   */
  rotulo?: React.ReactNode;
  /**
   * De que borda do gatilho o menu se pendura. `direita` (padrão) serve ao "⋯", que mora no canto direito
   * e abre para dentro da tela. Um gatilho no começo da linha precisa de `esquerda`: pendurado pela
   * direita, o menu de 230px passa da borda esquerda da tela e corta os rótulos (medido no Exportar das
   * Faturas, 06/10/2026, na tela renderizada; o jsdom não tem layout e não pega isso).
   */
  ancora?: 'direita' | 'esquerda';
  /**
   * `fixa` (10/10/2026): o menu sai em `position: fixed`, ancorado no gatilho pela posição na tela. Para gatilho
   * DENTRO de uma caixa com rolagem (a tabela de Despesas tem `overflow-x-auto`): pela spec de CSS, com um eixo auto o
   * outro deixa de ser `visible`, e o menu absolute das últimas linhas era recortado pela caixa.
   *
   * ⭐ 27/10/2026: os dois modos passaram a sair por PORTAL no body, sempre em posição fixa. O `relativa` dentro de
   * modal (a ficha do lead, o rodapé dos modais) era cortado pelo `overflow` do modal e sumia atrás da barra de abas
   * (captura do Heitor, aba Propostas do lead); e mesmo o `fixa` dependia de nenhum ancestral ter `transform`. A prop
   * fica por compatibilidade; o que difere entre os dois agora é nada.
   */
  ancoragem?: 'relativa' | 'fixa';
}

/**
 * Largura pelo conteúdo (12/10/2026, conferência em tela): só com `min-w`, o menu do contrato em Op. de Crédito abriu
 * com ~520px. `w-max` mede pelo item mais longo; o teto deixa um motivo longo (segunda linha) quebrar em vez de esticar.
 */
/**
 * Cores da Central (22/10/2026, pergunta do Heitor na prévia da fase 4 das listas): a caixa era slate-800 (azulada) com
 * hover slate-700, fora dos tokens das listas que o abrem. Agora: caixa no cartão da Central, hover no elevado (o mesmo da
 * linha), itens neutros e só o destrutivo em vermelho. Vale para todo ⋯ do HUB, porque é este componente.
 */
const CAIXA_DO_MENU = 'bg-central-cartao border border-central-borda rounded-lg shadow-xl py-1 min-w-[230px] w-max max-w-[min(360px,calc(100vw-16px))]';

/**
 * Onde o menu cabe (27/10/2026). Pura, para testar sem layout (o jsdom não mede): recebe o retângulo do gatilho, a altura
 * e a largura MEDIDAS do menu e a janela. Prefere o lado pedido (`abrePara`) e vira quando o outro tem mais espaço; se
 * nenhum cabe, fica no maior e rola por dentro (`alturaMax`). Na horizontal, pendura pela borda pedida e nunca sai da tela.
 */
export function posicaoDoMenu(args: {
  gatilho: { top: number; bottom: number; left: number; right: number };
  altura: number;
  largura: number;
  janela: { largura: number; altura: number };
  abrePara?: 'baixo' | 'cima';
  ancora?: 'direita' | 'esquerda';
}): { top: number; left: number; lado: 'baixo' | 'cima'; alturaMax: number } {
  const { gatilho: g, altura, largura, janela, abrePara = 'baixo', ancora = 'direita' } = args;
  const M = 8;
  const abaixo = janela.altura - g.bottom - 4 - M;
  const acima = g.top - 4 - M;
  let lado: 'baixo' | 'cima' = abrePara;
  if (lado === 'baixo' && altura > abaixo && acima > abaixo) lado = 'cima';
  else if (lado === 'cima' && altura > acima && abaixo > acima) lado = 'baixo';
  const espaco = lado === 'baixo' ? abaixo : acima;
  const h = Math.min(altura, Math.max(espaco, 0));
  const top = lado === 'baixo' ? g.bottom + 4 : Math.max(M, g.top - 4 - h);
  const leftDesejado = ancora === 'esquerda' ? g.left : g.right - largura;
  const left = Math.min(Math.max(M, leftDesejado), Math.max(M, janela.largura - largura - M));
  return { top, left, lado, alturaMax: Math.max(espaco, 0) };
}

/**
 * O menu de um gatilho (27/10/2026): o menu mora no body (portal), então "o menu desta linha" sai pelo `aria-controls`
 * do botão, não pela árvore do DOM. Serve a leitor de tela e aos testes que procuravam o item dentro da linha.
 */
export function menuDoGatilho(gatilho: Element | null | undefined): HTMLElement | null {
  const id = gatilho?.getAttribute('aria-controls');
  return id ? document.getElementById(id) : null;
}

const MaisAcoes: React.FC<Props> = ({ itens, abrePara = 'baixo', title = 'Mais ações', rotulo, ancora = 'direita' }) => {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; alturaMax: number; lado: 'baixo' | 'cima' } | null>(null);
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const idDoMenu = useId();
  const alternar = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPos(null);
    setAberto((v) => !v);
  };

  // Mede DEPOIS de montar (a altura real, não itens × 36) e posiciona antes de pintar.
  useLayoutEffect(() => {
    if (!aberto || !gatilhoRef.current || !menuRef.current) return;
    const r = gatilhoRef.current.getBoundingClientRect();
    const m = menuRef.current;
    setPos(posicaoDoMenu({
      gatilho: { top: r.top, bottom: r.bottom, left: r.left, right: r.right },
      altura: m.scrollHeight,
      largura: m.offsetWidth,
      janela: { largura: window.innerWidth, altura: window.innerHeight },
      abrePara,
      ancora,
    }));
  }, [aberto, abrePara, ancora, itens.length]);

  useEffect(() => {
    if (!aberto) return;
    // O menu mora no body (portal): "dentro" é o gatilho OU o menu.
    const dentro = (n: EventTarget | null) => n instanceof Node && ((ref.current && ref.current.contains(n)) || (menuRef.current && menuRef.current.contains(n)));
    const fora = (e: MouseEvent) => { if (!dentro(e.target)) setAberto(false); };
    // Esc fecha (22/10/2026). E fecha ao rolar ou redimensionar: a posição é da tela, e rolando ele ficaria flutuando
    // por cima de outra linha. O scroll é ouvido na captura, para pegar o <main>, o corpo de um modal e qualquer caixa.
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
    const rolou = (e: Event) => { if (!dentro(e.target)) setAberto(false); };
    const mudouJanela = () => setAberto(false);
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', tecla);
    window.addEventListener('scroll', rolou, true);
    window.addEventListener('resize', mudouJanela);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', tecla);
      window.removeEventListener('scroll', rolou, true);
      window.removeEventListener('resize', mudouJanela);
    };
  }, [aberto]);

  if (!itens.length) return null;
  return (
    <div className="relative" ref={ref}>
      {rotulo ? (
        <button
          ref={gatilhoRef}
          type="button"
          onClick={alternar}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium whitespace-nowrap text-sm transition-colors"
          title={title}
          aria-haspopup="menu"
          aria-expanded={aberto}
          aria-controls={aberto ? idDoMenu : undefined}
        >
          {rotulo}
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      ) : (
        <button
          ref={gatilhoRef}
          type="button"
          onClick={alternar}
          className="p-2 text-slate-400 hover:text-white hover:bg-central-elevado rounded-lg transition-colors"
          title={title}
          aria-haspopup="menu"
          aria-expanded={aberto}
          aria-controls={aberto ? idDoMenu : undefined}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <circle cx="10" cy="4" r="1.5" /><circle cx="10" cy="10" r="1.5" /><circle cx="10" cy="16" r="1.5" />
          </svg>
        </button>
      )}
      {aberto && createPortal(
        <div
          ref={menuRef}
          id={idDoMenu}
          role="menu"
          data-ancora={ancora}
          data-peca="menu-mais-acoes"
          data-lado={pos?.lado}
          // Antes de medir, invisível no canto (o layout precisa existir para medir); depois, no lugar. z acima de modal.
          style={pos ? { top: pos.top, left: pos.left, maxHeight: pos.alturaMax } : { top: 0, left: 0, visibility: 'hidden' }}
          className={`fixed z-[70] overflow-y-auto ${CAIXA_DO_MENU}`}
          onClick={(e) => e.stopPropagation()}
        >
          {itens.map((mi, i) =>
            mi.section ? (
              <div key={i} className="px-3 py-1 text-[10px] uppercase tracking-wide text-slate-500">{mi.label}</div>
            ) : (
              <React.Fragment key={i}>
              {mi.separadoAntes && <div className="my-1 border-t border-central-borda" role="separator" />}
              <button
                type="button"
                role="menuitem"
                disabled={mi.disabled}
                title={mi.title}
                onClick={() => { setAberto(false); mi.onClick(); }}
                className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm transition-colors [&>svg]:shrink-0 hover:bg-central-elevado disabled:opacity-40 disabled:cursor-not-allowed ${mi.danger ? 'text-red-300' : 'text-central-texto'}`}
              >
                {mi.label}
              </button>
              </React.Fragment>
            ),
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default MaisAcoes;
